import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Users, CalendarClock, Activity, Eye, FileText, Pencil, Trash2, GitCompareArrows, Bell } from "lucide-react";
import { api, apiErr } from "../api";
import { calcAge, prettyDate, FullLoader, Empty, StatusPill } from "../ui";
import NextAppointmentModal from "../components/NextAppointmentModal";

export default function PatientsTab({ patients, reload }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [appt, setAppt] = useState(null);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!patients) return [];
    if (!s) return patients;
    return patients.filter((p) => `${p.nombre} ${p.cedula || ""} ${p.telefono || ""}`.toLowerCase().includes(s));
  }, [patients, q]);

  if (!patients) return <FullLoader />;

  const del = async (p) => {
    if (!window.confirm(`¿Eliminar a ${p.nombre}? Sus citas pendientes se cancelarán.`)) return;
    try { await api.delete(`/patients/${p.id}`); toast.success("Paciente eliminado"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const withAppt = patients.filter((p) => p.next_appointment).length;
  const chronic = patients.filter((p) => p.enfermedades_cronicas).length;

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat icon={Users} label="Pacientes" value={patients.length} />
        <Stat icon={CalendarClock} label="Con próxima cita" value={withAppt} />
        <Stat icon={Activity} label="Crónicos (control c/3 meses)" value={chronic} />
        <Stat icon={GitCompareArrows} label="Con seguimientos" value={patients.filter((p) => (p.updates || []).length).length} />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 pn-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, cédula o teléfono" className="pn-field !pl-9" />
        </div>
        <Link to="/pacientes/nuevo" className="pn-btn pn-btn-primary ml-auto"><Plus size={17} /> Nuevo paciente</Link>
      </div>

      {shown.length === 0 ? (
        <Empty icon={FileText}>{patients.length ? "Ningún paciente coincide con la búsqueda." : <>Aún no hay pacientes. <Link to="/pacientes/nuevo" className="pn-accent font-semibold">Registrar el primero →</Link></>}</Empty>
      ) : (
        <div className="pn-card rounded-2xl overflow-x-auto">
          <table className="pn-table min-w-[860px]">
            <thead><tr><th>Paciente</th><th>Cédula</th><th>Edad</th><th>Sexo</th><th>T. sangre</th><th>Teléfono</th><th>Próxima cita</th><th className="text-right">Acciones</th></tr></thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id} className="pn-row-hover cursor-pointer" onClick={() => nav(`/pacientes/${p.id}`)}>
                  <td>
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 shrink-0 rounded-xl pn-grad flex items-center justify-center font-bold">{(p.nombre || "?").charAt(0).toUpperCase()}</span>
                      <div className="min-w-0">
                        <div className="font-semibold pn-ink truncate max-w-[220px]">{p.nombre}</div>
                        <div className="text-xs pn-muted flex gap-2">
                          {p.enfermedades_cronicas && <span className="pn-brand whitespace-nowrap">crónico</span>}
                          {p.titulo_diagnostico && <span className="truncate max-w-[180px]">{p.titulo_diagnostico}</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="pn-sub">{p.cedula || "—"}</td>
                  <td className="pn-sub">{p.edad || calcAge(p.fecha_nacimiento) || "—"}</td>
                  <td className="pn-sub">{p.sexo || "—"}</td>
                  <td className="pn-sub">{p.tipo_sangre || "—"}</td>
                  <td className="pn-sub">{p.telefono || "—"}</td>
                  <td>
                    {p.next_appointment ? (
                      <div className="flex flex-col gap-1 items-start">
                        <span className="text-xs font-semibold pn-ink flex items-center gap-1"><Bell size={12} className="pn-brand" />{prettyDate(p.next_appointment.fecha)}{p.next_appointment.hora ? ` · ${p.next_appointment.hora}` : ""}</span>
                        <StatusPill status={p.next_appointment.status} />
                      </div>
                    ) : <span className="pn-muted text-xs">—</span>}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => setAppt(p)} className="pn-btn pn-btn-soft !px-2.5 !py-1.5 text-xs" title="Próxima consulta"><CalendarClock size={14} /> Próxima</button>
                      <Link to={`/visor/${p.id}`} className="pn-icon-btn" title="Visor clínico"><Eye size={15} /></Link>
                      <Link to={`/pacientes/${p.id}/editar`} className="pn-icon-btn" title="Editar ficha"><Pencil size={15} /></Link>
                      <button onClick={() => del(p)} className="pn-icon-btn" title="Eliminar"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {appt && <NextAppointmentModal patient={appt} onClose={() => setAppt(null)} onSaved={reload} />}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="pn-card rounded-2xl p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl pn-soft-accent flex items-center justify-center shrink-0"><Icon size={18} /></div>
      <div className="min-w-0"><div className="text-2xl font-bold pn-ink leading-none">{value}</div><div className="text-xs pn-sub mt-1 truncate">{label}</div></div>
    </div>
  );
}
